import { createContext, useContext } from 'react';

import { EntityLoaders, FullProject, FullTask, TaskWithSub, User } from 'entities';

import { UseUrlState } from 'hooks';
import { Tab } from '../tabs';
import { UseEntityLoad } from 'entities/hooks/load/createHook';

export type ProjectModal =
  | 'DETAILS'
  | 'FILES'
  | 'MANAGE_COSTS'
  | 'LOGS'
  | 'ADD_SERVICE'
  | 'CHANGE_TEAM'
  | 'CANCEL_PROJECT'
  | 'CHOOSE_PDF'
  | 'ADD_COUNTRY';

export type TaskModal =
  | 'EXCLUDED_IDS'
  | 'SAMPLE_PROJECT_IDS'
  | 'ANSWER_DATE_CHANGE'
  | 'REQUEST_RECRUITMENT'
  | 'SAMPLE_PROJECTS'
  | 'CREATE_SAMPLE_PROJECT';

type ProjectQueryParams = {
  tab: Tab;
  recruitmentId?: string;
  taskId?: string;
  issueId?: string;
};

type TaskUpdateParams = Partial<TaskWithSub | FullTask>;

export type ProjectProfileContext = {
  id: string;
  project: FullProject;
  loading: boolean;
  isOwner: boolean;

  tab: Tab;
  setTab: (tab: Tab) => void;

  refreshProject: () => Promise<void>;
  updateProject: (newData: FullProject | ((old: FullProject) => FullProject)) => void;
  projectUsers: Record<string, User>;

  projectModalConfig: {
    openModal: (modal: ProjectModal) => void;
    closeModal: () => void;
    modal: ProjectModal | null;
  };

  taskModalConfig: {
    openModal: (modal: TaskModal, id: string) => void;
    closeModal: () => void;
    modal: TaskModal | null;
    id: string;
    data?: FullTask | TaskWithSub;
  };

  openTask: (id: string) => void;
  updateTask: (
    id: string,
    ref:
      | TaskUpdateParams
      | ((old: TaskWithSub & Partial<FullTask>) => TaskWithSub & Partial<FullTask>),
  ) => void;

  fullBlocked: boolean;

  queryParams: ProjectQueryParams;
  setQueryParams: UseUrlState<ProjectQueryParams>['updateUrlParams'];

  recruitments: UseEntityLoad<EntityLoaders, 'projects', 'listProjectRecruitments', never[]>;
};

export const projectProfileContext = createContext({} as ProjectProfileContext);

export function useProjectProfile(): ProjectProfileContext {
  const context = useContext(projectProfileContext);

  if (!context.projectModalConfig)
    throw new Error('Please make sure to use Project Profile hook within its provider');

  return context;
}
